import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Camera, Image as ImageIcon, Trash2, MapPin, Loader2, WifiOff, Check, Search } from 'lucide-react';
import Button from '../ui/Button';
import cultivarService from '../../services/cultivar';
import evaluationQueue, { newUuid } from '../../services/evaluationQueue';
import { useLocation } from '../../hooks/useLocation';
import type { CropType, QuickEvaluationInput, QuickMatchSuggestion } from '../../types/cultivar';

// A quick evaluation of a cultivar that is not in the registry: the name off
// the tag, where, a score, a note and photos. For walking a test orchard and
// getting something down before moving to the next tree. Saved to the phone
// first (evaluationQueue), sent when there is signal, and kept on
// /cultivar/quick-evaluations until it is matched to a registered cultivar.

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fired after each save, so the parent can refresh its list. */
  onSaved: () => void;
  /** Prefill the name, e.g. adding another visit to a name already recorded. */
  initialName?: string;
  initialCropTypeId?: number | null;
}

interface DraftPhoto {
  clientUuid: string;
  blob: Blob;
  fileName: string;
  previewUrl: string;
  latitude: number | null;
  longitude: number | null;
  takenAt: string;
}

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function QuickEvaluationForm({ open, onClose, onSaved, initialName, initialCropTypeId }: Props) {
  const [nameSeen, setNameSeen] = useState('');
  const [cropTypeId, setCropTypeId] = useState<number | ''>('');
  const [cropTypes, setCropTypes] = useState<CropType[]>([]);
  const [evaluatedOn, setEvaluatedOn] = useState(todayStr());
  const [locationLabel, setLocationLabel] = useState('');
  const [coords, setCoords] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<DraftPhoto[]>([]);
  const [suggestions, setSuggestions] = useState<QuickMatchSuggestion[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const { requestLocation, loading: locating } = useLocation();

  const clearTree = useCallback(() => {
    setNameSeen('');
    setScore(null);
    setNotes('');
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setSuggestions([]);
    setError(null);
  }, []);

  // Stays mounted while closed, so every open starts clean.
  useEffect(() => {
    if (!open) return;
    clearTree();
    setNameSeen(initialName ?? '');
    setCropTypeId(initialCropTypeId ?? '');
    setEvaluatedOn(todayStr());
    setLocationLabel('');
    setCoords(null);
    setSavedCount(0);
  }, [open, initialName, initialCropTypeId, clearTree]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    cultivarService.getCropTypes()
      .then((res) => { if (!cancelled && res.success && res.data) setCropTypes(res.data); })
      .catch(() => { /* offline: the crop is optional */ });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Is it registered after all? Asked while typing, when there is signal.
  useEffect(() => {
    if (!open || isOffline) return;
    const name = nameSeen.trim();
    if (name.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      cultivarService.suggestQuickMatches(name, cropTypeId || null)
        .then((res) => { if (!cancelled && res.success && res.data) setSuggestions(res.data.filter((s) => s.exact)); })
        .catch(() => { /* a hint, never a blocker */ });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, isOffline, nameSeen, cropTypeId]);

  const photosRef = useRef<DraftPhoto[]>([]);
  photosRef.current = photos;
  useEffect(() => () => { photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)); }, []);

  const addPhoto = (file: File) => {
    if (file.size > MAX_PHOTO_BYTES) {
      setError('Photos must be under 10MB.');
      return;
    }
    setError(null);
    setPhotos((prev) => [...prev, {
      clientUuid: newUuid(),
      blob: file,
      fileName: file.name || 'photo.jpg',
      previewUrl: URL.createObjectURL(file),
      latitude: coords?.lat ?? null,
      longitude: coords?.lng ?? null,
      takenAt: new Date().toISOString(),
    }]);
  };

  const removePhoto = (uuid: string) => {
    setPhotos((prev) => {
      const gone = prev.find((p) => p.clientUuid === uuid);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((p) => p.clientUuid !== uuid);
    });
  };

  const captureLocation = async () => {
    try {
      const res = await requestLocation();
      if (res) {
        setCoords({ lat: res.coords.latitude, lng: res.coords.longitude, accuracy: res.coords.accuracy });
        setError(null);
      }
    } catch {
      setError('Could not get GPS position. You can still save without it.');
    }
  };

  const save = async (next: boolean) => {
    const name = nameSeen.trim();
    if (!/[a-z0-9]/i.test(name)) {
      setError('Write the name as it is on the tag.');
      nameRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    const payload: QuickEvaluationInput = {
      client_uuid: newUuid(),
      name_seen: name,
      crop_type_id: cropTypeId || null,
      evaluated_on: evaluatedOn,
      latitude: coords?.lat ?? null,
      longitude: coords?.lng ?? null,
      location_label: locationLabel.trim() || null,
      overall_score: score,
      notes: notes.trim() || null,
    };
    try {
      await evaluationQueue.enqueueQuick(payload, photos.map((p) => ({
        clientUuid: p.clientUuid,
        blob: p.blob,
        fileName: p.fileName,
        caption: null,
        latitude: p.latitude,
        longitude: p.longitude,
        takenAt: p.takenAt,
      })));
      onSaved();
      if (next) {
        clearTree();
        setSavedCount((n) => n + 1);
        nameRef.current?.focus();
      } else {
        onClose();
      }
    } catch {
      setError('Could not save on this device. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 shrink-0">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-neutral-900">Quick evaluation</h2>
          <p className="text-xs text-neutral-500">A cultivar not in the registry</p>
        </div>
        <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 p-2 -mr-2" aria-label="Close">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700">{error}</div>
        )}
        {savedCount > 0 && (
          <div className="bg-success-50 border border-success-200 rounded-lg p-3 text-sm text-success-800 flex items-center gap-2">
            <Check className="h-4 w-4 shrink-0" />
            <span>{savedCount === 1 ? 'Saved. Next tree.' : `${savedCount} saved. Next tree.`}</span>
          </div>
        )}
        {isOffline && (
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-800 flex items-start gap-2">
            <WifiOff className="h-4 w-4 mt-0.5 shrink-0" />
            <span>You're offline. This is saved on your device and sent when signal returns.</span>
          </div>
        )}

        <section className="space-y-3">
          <div>
            <label htmlFor="qe-name" className="block text-xs font-medium text-neutral-700 mb-1">Name on the tag</label>
            <input
              id="qe-name"
              ref={nameRef}
              type="text"
              value={nameSeen}
              onChange={(e) => setNameSeen(e.target.value)}
              placeholder="e.g. T-14 or Royal Gala sport"
              autoComplete="off"
              className="input-professional w-full"
            />
            <p className="text-xs text-neutral-500 mt-1">
              As written, codes and all. It is matched to the registry when the cultivar is registered.
            </p>
          </div>

          {suggestions.length > 0 && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm text-primary-900 space-y-1">
              <p className="flex items-center gap-1.5 font-medium">
                <Search className="h-4 w-4 shrink-0" /> This name is already in the registry
              </p>
              {suggestions.map((s) => (
                <p key={s.id} className="break-words">
                  <Link to={`/cultivar/${s.id}?tab=observations`} className="underline font-medium">{s.name}</Link>
                  {s.matched_synonym && <span className="text-primary-700"> (also called {s.matched_synonym})</span>}
                  {s.crop_type_name && <span className="text-primary-700"> · {s.crop_type_name}</span>}
                </p>
              ))}
              <p className="text-xs text-primary-700">Open it for a full evaluation, or save this one and match it later.</p>
            </div>
          )}

          <div>
            <label htmlFor="qe-crop" className="block text-xs font-medium text-neutral-700 mb-1">Crop (optional)</label>
            <select
              id="qe-crop"
              value={cropTypeId}
              onChange={(e) => setCropTypeId(e.target.value ? Number(e.target.value) : '')}
              className="input-professional w-full"
            >
              <option value="">Not sure</option>
              {cropTypes.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0">
              <label htmlFor="qe-date" className="block text-xs font-medium text-neutral-700 mb-1">Date</label>
              <input
                id="qe-date"
                type="date"
                value={evaluatedOn}
                onChange={(e) => setEvaluatedOn(e.target.value)}
                className="input-professional w-full"
              />
            </div>
            <div className="min-w-0">
              <label htmlFor="qe-where" className="block text-xs font-medium text-neutral-700 mb-1">Where (row, tree, block)</label>
              <input
                id="qe-where"
                type="text"
                value={locationLabel}
                onChange={(e) => setLocationLabel(e.target.value)}
                placeholder="e.g. Trial block row 4, tree 12"
                className="input-professional w-full"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={captureLocation} disabled={locating}>
              {locating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <MapPin className="h-4 w-4 mr-1" />}
              {coords ? 'Update GPS' : 'Add GPS'}
            </Button>
            {coords && (
              <span className="text-xs text-neutral-600">
                {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} (±{Math.round(coords.accuracy)} m)
              </span>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-900">Photos</h3>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            aria-label="Take photo"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) addPhoto(f);
              e.target.value = '';
            }}
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            aria-label="Choose photos"
            onChange={(e) => {
              Array.from(e.target.files || []).forEach(addPhoto);
              e.target.value = '';
            }}
          />
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => cameraRef.current?.click()} className="flex-1">
              <Camera className="h-4 w-4 mr-1" /> Take photo
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="flex-1">
              <ImageIcon className="h-4 w-4 mr-1" /> Choose
            </Button>
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p) => (
                <div key={p.clientUuid} className="relative border border-neutral-200 rounded-lg overflow-hidden">
                  <img src={p.previewUrl} alt="" className="w-full h-24 object-cover" />
                  <button
                    onClick={() => removePhoto(p.clientUuid)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1.5"
                    aria-label="Remove photo"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-900">
            Score {score != null && <span className="text-primary-600">{score}/10</span>}
          </h3>
          <div className="grid grid-cols-10 gap-1">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={score === n}
                onClick={() => setScore(score === n ? null : n)}
                className={`h-9 rounded text-xs font-medium border transition-colors ${
                  score === n ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-neutral-600 border-neutral-300'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div>
            <label htmlFor="qe-notes" className="block text-xs font-medium text-neutral-700 mb-1">Notes</label>
            <textarea
              id="qe-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Fruit size, colour, crop, anything worth remembering"
              className="input-professional w-full"
            />
          </div>
        </section>
      </div>

      <div
        className="border-t border-neutral-200 bg-white px-4 py-3 flex gap-2 shrink-0"
        style={{ paddingBottom: 'max(0.75rem, var(--safe-bottom))' }}
      >
        <Button variant="outline" onClick={onClose} className="flex-1">Close</Button>
        <Button variant="outline" onClick={() => save(true)} disabled={saving} className="flex-1">Save &amp; next</Button>
        <Button variant="primary" onClick={() => save(false)} isLoading={saving} className="flex-1">
          {isOffline ? 'Save offline' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default QuickEvaluationForm;
